/**
 * The one door into the blink-superset split bundle (the charts, the filters and the table), so
 * the rules for loading it live in one place (documentation/context/blink-superset-bundle.md):
 *
 *   - Each entry is imported lazily, through a STATIC string literal: Vite emits a literal import()
 *     as its own chunk, fetched the first time a chart draws or a filter mounts. A template
 *     literal would defeat that, and a static import would put the charts and their ECharts, or
 *     antd's pickers, on the login screen — app.js is the one entry every layout loads.
 *   - It must never be named in vite.config.js manualChunks: naming a chunk makes Rollup treat it
 *     as an entry and pulls it back into the eager graph.
 *   - It is the SPLIT shape. solo/superset-table.js is for pages that draw only a table.
 *
 * SupersetChartModulesTest holds all three, and that nothing else imports the split shape.
 *
 * A download that failed is never imported again, under this URL or a fresh one. The browser keeps
 * a failed module for the rest of the page, the entry's and every chunk it imports, and the
 * bundle's own chart chunks import the entry's chunk by its plain URL, so a fresh-URL copy of the
 * entry would load and leave those charts failing (measured in Chromium and Firefox). A reload is
 * the one recovery, and the host offers it (superset-chart.js, sc_retry).
 */
import { memoisedImport } from './echarts-lifecycle.js';

const importBundle = memoisedImport(() => import('../vendor/blink-superset/split/superset-charts.js'));
const importFilters = memoisedImport(() => import('../vendor/blink-superset/split/superset-filters.js'));
const importTable = memoisedImport(() => import('../vendor/blink-superset/split/superset-table.js'));

let schemes = null;
let registered = false;

/**
 * Hold the page's colour schemes until the bundle loads. Every host on a page renders the same
 * list, so the first one to arrive is the list.
 */
export function rememberSchemes(list) {
    if (schemes === null && Array.isArray(list) && list.length > 0) {
        schemes = list;
    }
}

/**
 * The bundle, with the page's schemes registered before anything can mount from it: the registry
 * answers an unknown scheme id with Superset's default rather than an error, so a chart drawn
 * before registration would render in the wrong colours and say nothing.
 */
export function loadSupersetCharts() {
    return importBundle().then((bundle) => {
        if (schemes !== null && ! registered) {
            bundle.registerColorSchemes(schemes);
            registered = true;
        }

        return bundle;
    });
}

/**
 * The contract drawn in the given scheme when it names none of its own. A copy: contracts arrive
 * from events every consumer shares, and the held copy is replayed to hosts that mount later.
 */
export function withColorScheme(contract, schemeId) {
    if (! schemeId || ! contract?.formData || contract.formData.colorScheme) {
        return contract;
    }

    return { ...contract, formData: { ...contract.formData, colorScheme: schemeId } };
}

/**
 * Superset's date, range and select filters, for the x-filters hosts (blink-superset-bundle.md,
 * "Mounting a date filter", "Mounting a range filter" and "Mounting a select filter"). Nothing to
 * register first: the entry reads no palette.
 */
export function loadSupersetFilters() {
    return importFilters();
}

/**
 * BlinkTable in the split shape, for <x-charts.superset-table> on a page that also draws charts (a
 * report's scorecard grid card): it shares React, Emotion and antd with the charts, where
 * solo/superset-table.js would bring a second copy of each. A page that draws only a table loads
 * the solo shape instead, as the drilldown does.
 */
export function loadSupersetTable() {
    return importTable();
}
